import { Alert } from '@mm/components';
import WidgetModel from '@mm/state/model/WidgetModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, { useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import {
  ErrorPortal,
  WidgetContainerRefProp,
} from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import Spinner from '../../components/Spinner';
import { useVideoWidgetHandlers } from '../../helpers/widget-helpers/use-video-widget-handlers';
import { useResolutionInfo } from '@mm/hooks';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import { WidgetUnsupportedError } from '../errors/WidgetUnsupportedError/WidgetUnsupportedError';
import { MAX_VIDEO_SIZE, VIDEO_EXTENSIONS } from './constants';
import './VideoWidget.pcss';
import { PlatformType } from '@mm/state/model/CampaignsModel';

type Props = {
  widget: WidgetModel;
  draggableProvided?: DraggableProvided;
  isSmsRelated?: boolean;
  forInstagram?: boolean;
  smsOnly?: boolean;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

const VIDEO_SIZE_CAP = 0.6 * 1024 * 1024;

const useUploadVideo = (widget: WidgetModel) => {
  const videoWidgetHandlers = useVideoWidgetHandlers();
  const dispatch: Dispatch<StoreState> = useDispatch();
  const [isUploading, setIsUploading] = useState(false);

  const uploadVideo = async ([file]: File[], rejectedFiles: File[]) => {
    if (rejectedFiles.length) {
      return dispatch(
        modalActions.openPrettyErrorModal({
          title: 'Unsupported Extension',
          errorMessage: `Supported file extensions are .mov and .mp4`,
        }),
      );
    }

    if (file) {
      if (file.size > MAX_VIDEO_SIZE) {
        return dispatch(
          modalActions.openPrettyErrorModal({
            title: 'Upload Size Limit',
            errorMessage: 'The video should be less than 25MB',
          }),
        );
      }

      try {
        setIsUploading(true);
        await videoWidgetHandlers.addVideoToWidget(widget, file);
        setIsUploading(false);
      } catch (error) {
        setIsUploading(false);
      }
    }
  };

  return { isUploading, uploadVideo };
};

export const VideoWidget = (props: Props) => {
  const {
    widget,
    draggableProvided,
    isSmsRelated,
    smsOnly,
    forInstagram,
    platforms,
  } = props;
  const {
    config: { description },
    file,
  } = widget;
  const { isUploading, uploadVideo } = useUploadVideo(widget);
  const { getRootProps, getInputProps } = useDropzone({
    accept: VIDEO_EXTENSIONS,
    onDrop: uploadVideo,
  });

  const { sourceSize } = widget.config;
  const isSizeOvercap =
    isSmsRelated && sourceSize && sourceSize > VIDEO_SIZE_CAP;

  const { isDesktop } = useResolutionInfo();

  return (
    <div
      className={classnames('video-widget', {
        'video-widget--has-file': file,
      })}
    >
      <FacebookPageIcon />

      {file ? (
        <div className="video-widget__file-area-wrapper">
          {isUploading && (
            <div className="video-widget__uploading-wrapper">
              <Spinner
                show={true}
                position="absolute"
                backgroundColor="none"
                size={30}
              />
            </div>
          )}

          <video
            title={description}
            className="video-widget__video"
            controls
            src={file}
          />

          <div
            className="video-widget__file-area-upload-hover"
            {...getRootProps()}
          >
            <input {...getInputProps()} accept="video/mp4,video/quicktime" />

            <i className="far fa-file-video fa-3x video-widget__upload-icon" />
            <span>Upload new Video</span>

            <div className="video-widget__file-area-upload-hover-blinder" />
          </div>
        </div>
      ) : (
        <div className="video-widget__drop-area" {...getRootProps()}>
          {isUploading && (
            <div className="video-widget__uploading-wrapper">
              <Spinner
                show={true}
                position="absolute"
                backgroundColor="none"
                size={30}
              />
            </div>
          )}

          <input {...getInputProps()} accept="video/mp4,video/quicktime" />

          <div className="video-widget__drop-area-text">
            <i className="far fa-file-video fa-3x video-widget__upload-icon" />
            <span>
              Drop video file or
              <span className="video-widget__choose-file">choose file</span>
            </span>
          </div>
        </div>
      )}

      {isSizeOvercap && (
        <Alert
          className="video-widget__warning"
          type={smsOnly ? 'error' : 'warning'}
          showCloseIcon={false}
        >
          This media file exceeds size limit on SMS and will NOT be sent in your
          SMS dialogue. Please reduce size to &lt;600KB.
        </Alert>
      )}

      <WidgetControls
        widget={widget}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />

      {forInstagram && !widget.supportedByPlatforms.instagram && (
        <ErrorPortal widget={widget}>
          <WidgetUnsupportedError isError type="instagram" />
        </ErrorPortal>
      )}
    </div>
  );
};
